import type { StyleSpecification } from 'maplibre-gl';
import {
  BASEMAP_STORAGE_KEY,
  createSessionCache,
  mapTypeForChoice,
  tileUrlTemplate,
  type BasemapChoice,
  type FetchLike,
  type GoogleSession,
  type SessionStore,
} from '@raad/geo';

/**
 * The WEB (business, green) basemap adapter — slice 51.
 *
 * The framework-free brain (`@raad/geo/basemap`) owns the session cache, the
 * expiry maths and the fallback state machine. THIS file is the thin app half:
 * it reads the Vite-injected key, owns the OpenFreeMap street-style URL, builds a
 * MapLibre raster `StyleSpecification` from a Google session, and persists the
 * user's choice in localStorage. One session cache singleton is shared by every
 * web map surface, so the live map, the alerts map, the trip replay and the two
 * draw maps all reuse ONE createSession per mapType (unbilled, but still one).
 *
 * 🔴 The KEY never leaves this layer as anything but a request query param that
 * the browser sends to Google directly. It is not logged, not persisted, not put
 * in the DOM. The share page (`features/share/SharedMap.tsx`) does NOT import this
 * file — it stays OpenFreeMap-only with no switcher, protecting the shared
 * per-project quota (Mahad, 12 Sep 2026).
 */

/** The existing OpenFreeMap Liberty vector street style — the "Street map" option
 *  and the involuntary fallback target. Was duplicated as a literal in six map
 *  files; this is now the single source for the web app. */
export const VECTOR_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

/** The Google raster source id, shared so a tile-error handler can tell a Google
 *  tile failure from an OpenFreeMap one. */
export const GOOGLE_SOURCE_ID = 'google-basemap';

/** The Maps key, injected at build by Vite from VITE_GOOGLE_MAPS_KEY. Empty when
 *  unset → the Google options are hidden and Street map is the default. Trimmed so
 *  a stray newline in a secret never counts as "present". */
export function googleMapsKey(): string {
  return (import.meta.env.VITE_GOOGLE_MAPS_KEY ?? '').trim();
}
export function hasGoogleKey(): boolean {
  return googleMapsKey().length > 0;
}

/** How long a createSession may take before it counts as a failure (slice 56f).
 *  Without it a merely-SLOW session hung the Layers spinner indefinitely instead of
 *  falling back to the Street map. */
export const SESSION_TIMEOUT_MS = 8_000;

/** Adapt the browser `fetch` to the injectable `FetchLike` the geo module wants.
 *  A browser sets Referer itself, so we never force the header here. */
const browserFetch: FetchLike = (url, init) =>
  fetch(url, {
    method: init?.method,
    headers: init?.headers,
    body: init?.body,
    signal: typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(SESSION_TIMEOUT_MS) : undefined,
  });

/** A short, non-reversible tag of the key, so a session minted under one key is
 *  never replayed under another (a key rotation just re-creates). Not a secret
 *  measure — the key is public by design (CLAUDE.md, slice 51). */
export function keyTag(key: string): string {
  let h = 5381;
  for (let i = 0; i < key.length; i += 1) h = ((h * 33) ^ key.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

const SESSION_STORAGE_PREFIX = 'raad.gsession.';

/** localStorage-backed session store (slice 56f). A session TOKEN only, with its
 *  expiry; every access guarded (private windows / blocked storage throw), every
 *  malformed or foreign-key entry ignored → the cache simply creates a fresh one. */
export const localSessionStore: SessionStore = {
  read(mapType) {
    try {
      const raw = window.localStorage.getItem(SESSION_STORAGE_PREFIX + mapType);
      if (!raw) return null;
      const j = JSON.parse(raw) as Partial<GoogleSession> & { keyTag?: string };
      if (j.keyTag !== keyTag(googleMapsKey())) return null;
      if (j.mapType !== mapType || typeof j.session !== 'string' || typeof j.expirySec !== 'number') return null;
      return {
        mapType,
        session: j.session,
        expirySec: j.expirySec,
        tileWidth: Number(j.tileWidth) || 256,
        tileHeight: Number(j.tileHeight) || 256,
        imageFormat: String(j.imageFormat || (mapType === 'satellite' ? 'jpeg' : 'png')),
      };
    } catch {
      return null;
    }
  },
  write(session) {
    try {
      window.localStorage.setItem(
        SESSION_STORAGE_PREFIX + session.mapType,
        JSON.stringify({ ...session, keyTag: keyTag(googleMapsKey()) }),
      );
    } catch {
      /* storage full / blocked — the in-memory cache still works */
    }
  },
};

/** ONE session cache for the whole web app — shared across every surface so the
 *  fleet map and the draw maps do not each create their own session. Backed by
 *  localStorage (56f) so a valid session survives the PWA being killed. */
export const webSessionCache = createSessionCache(browserFetch, undefined, localSessionStore);

/**
 * Pre-warm BOTH Google sessions at map init (slice 56, item 10). createSession is
 * UNBILLED (observed, basemap.ts header), so this costs nothing but a request, and
 * it removes the "satellite switch stalls" hitch: without it, the first tap on
 * Satellite awaits a cold createSession before `styleForChoice` can build the
 * style. Fire-and-forget, guarded, deduped by the cache's inflight map (the
 * default roadmap `applyChoice` shares the same promise). A failure here is
 * swallowed — the real switch will surface it through the normal fallback path.
 */
export function prewarmGoogleSessions(): void {
  if (!hasGoogleKey()) return;
  const key = googleMapsKey();
  void webSessionCache.get('roadmap', key).catch(() => {});
  void webSessionCache.get('satellite', key).catch(() => {});
}

/**
 * Build the MapLibre raster style for a Google session. The key is embedded in the
 * tile URL exactly as the probe's 200 tile GETs were formed. `tileSize` comes from
 * the session's `tileWidth` (observed 256). `maxzoom` is 22 — a request past it
 * returns a valid tile (observed z22 → 200), so maplibre overzooms cleanly. We set
 * NO source `attribution`: our own `GoogleAttribution` control renders the official
 * logo + the live viewport copyright, so maplibre's own attribution string stays
 * empty for the Google layers.
 */
/**
 * The self-hosted glyph URL (slice 56, item 11). `PLAIN_STYLE` and the Google raster
 * style below carry no glyph server of their own, so the vehicle NAME TAG (a symbol
 * layer with a `text-field`) could not render text on them. We ship the Latin ranges
 * of Noto Sans Regular under `web/public/fonts/{fontstack}/` (see that folder's
 * README for provenance + sha256s) and point every tile-free / raster style here. The
 * OpenFreeMap vector style keeps its own remote glyphs (also Noto Sans Regular), so
 * the tag renders on all three basemaps.
 */
export const GLYPHS_URL = '/fonts/{fontstack}/{range}.pbf';

export function buildGoogleRasterStyle(session: GoogleSession, key: string): StyleSpecification {
  return {
    version: 8,
    glyphs: GLYPHS_URL,
    sources: {
      [GOOGLE_SOURCE_ID]: {
        type: 'raster',
        tiles: [tileUrlTemplate(session, key)],
        tileSize: session.tileWidth || 256,
        maxzoom: 22,
        attribution: '',
      },
    },
    layers: [{ id: GOOGLE_SOURCE_ID, type: 'raster', source: GOOGLE_SOURCE_ID }],
  };
}

/** Fetch the OpenFreeMap vector style JSON with an abort timeout, or throw. The
 *  surfaces already do this inline; centralised here so the URL lives once. */
export async function fetchVectorStyle(signal: AbortSignal): Promise<StyleSpecification> {
  const res = await fetch(VECTOR_STYLE_URL, { signal });
  if (!res.ok) throw new Error(`style ${res.status}`);
  return (await res.json()) as StyleSpecification;
}

/**
 * Resolve the MapLibre style for a choice. Google choices create/refresh a session
 * (throws → the caller falls back); the street map fetches the vector JSON. Kept
 * async and side-effect-free beyond the cache so a surface can `await` it and hand
 * the result to `registry.upgradeStyle`.
 */
export async function styleForChoice(
  choice: BasemapChoice,
  signal: AbortSignal,
): Promise<StyleSpecification> {
  const mapType = mapTypeForChoice(choice);
  if (mapType) {
    const key = googleMapsKey();
    const session = await webSessionCache.get(mapType, key);
    return buildGoogleRasterStyle(session, key);
  }
  return fetchVectorStyle(signal);
}

/** Read the persisted choice, guarded — private windows and blocked storage throw
 *  on access (CLAUDE.md: every localStorage read/write in try/catch). */
export function readPersistedChoice(): string | null {
  try {
    return localStorage.getItem(BASEMAP_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function persistChoice(choice: BasemapChoice): void {
  try {
    localStorage.setItem(BASEMAP_STORAGE_KEY, choice);
  } catch {
    /* private window / storage disabled — a per-viewer convenience, not state */
  }
}
